import { CacheProvider } from '@emotion/react';
import React from 'react';
import { AgentLogout } from '#/components/AgentLogout';
import { Brand } from '#/lib/brand';
import { createEmotionCache } from '#/lib/createEmotionCache';
import { useCreateBrandTheme } from '#/lib/theme/createTheme';
import { analytics } from '#/services/firebaseClient';
import { RootStoreProps } from '#/stores/RootStore';
import { boldenVan, poppins } from '#/styles/fonts';
import { EmotionCache } from '@emotion/utils';
import clientConfig from '@jucy-nakuru/config/clientConfig';
import { JucyUiProvider } from '@jucy-ui/components';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { SimplePaletteColorOptions, ThemeProvider } from '@mui/material/styles';
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import '@uppy/webcam/dist/style.min.css';
import { logEvent } from 'firebase/analytics';
import { AppInitialProps } from 'next/app';
import { NextRouter } from 'next/dist/shared/lib/router/router';
import { AppContextType, AppPropsType } from 'next/dist/shared/lib/utils';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextComponentType, GetServerSideProps as NextGetServerSideProps, PreviewData } from 'next/types';
import { ParsedUrlQuery } from 'querystring';
import AppProviders from '../components/AppProviders';
import '../lib/init';
import serializer from '../lib/serializer';
import initI18next from './../lib/i18n';

const clientSideEmotionCache = createEmotionCache();

export type GetServerSideProps<
    P extends { [key: string]: unknown } = { [key: string]: unknown },
    Q extends ParsedUrlQuery = ParsedUrlQuery,
    D extends PreviewData = PreviewData,
> = NextGetServerSideProps<P, Q, D> & { initialData?: RootStoreProps };

export type BasePageProps = {
    initialData?: RootStoreProps;
    brand: Brand;
};

export interface PortalAppProps extends AppPropsType<NextRouter, BasePageProps> {
    initialData?: RootStoreProps;
    emotionCache: EmotionCache;
    brand: Brand;
}

export type PortalAppType = NextComponentType<AppContextType, AppInitialProps, PortalAppProps>;

const PortalApp: PortalAppType = ({ Component, pageProps, emotionCache = clientSideEmotionCache, ...props }) => {
    const deserializedPageProps = serializer.deserialize(pageProps);
    const router = useRouter();
    const brand = props.brand || router.query.brand;
    React.useEffect(() => {
        // Remove the server-side injected CSS.
        const jssStyles = document.querySelector('#jss-server-side');
        if (jssStyles) {
            jssStyles?.parentElement?.removeChild(jssStyles);
        }

        if (analytics) {
            logEvent(analytics, 'page_view');
        }
        if (clientConfig?.hotjar?.id && clientConfig?.hotjar?.sv) {
            import('react-hotjar').then(({ hotjar }) => {
                if (clientConfig?.hotjar?.id && clientConfig?.hotjar?.sv) {
                    hotjar.initialize({
                        id: clientConfig.hotjar.id,
                        sv: clientConfig.hotjar.sv,
                    });
                }
            });
        }
    }, []);
    React.useEffect(() => {
        initI18next({ brand });
    }, [brand]);
    const theme = useCreateBrandTheme(brand);
    if (!brand) {
        return null;
    }
    const pageTitle = `${brand?.toUpperCase()} Portal`;
    return (
        <JucyUiProvider brand={brand}>
            <CacheProvider value={emotionCache}>
                <GlobalStyles
                    styles={{
                        '#__next, body, html': {
                            height: '100%',
                        },
                        '[data-brand="jucy"]': {
                            '--page-bg':
                                'url("https://storage.googleapis.com/jucy-chilwa-prod-57ad03.appspot.com/Image/jucy_page_bg_635e14d60b/jucy_page_bg_635e14d60b.jpg")',
                        },

                        '[data-brand="star"]': {
                            '--page-bg':
                                'url("https://storage.googleapis.com/jucy-chilwa-prod-57ad03.appspot.com/Image/star_page_bg_9a0d57d9f9/star_page_bg_9a0d57d9f9.jpg")',
                        },
                        ':root': {
                            '--font-poppins': `${poppins.style.fontFamily}`,
                            '--font-boldenvan': `${boldenVan.style.fontFamily}`,
                            '--bs-font-sans-serif': 'var(--font-poppins)',
                            '--bs-body-font-family': 'var(--font-poppins)',
                        },
                        a: {
                            color: theme.palette.primary.main,
                        },
                    }}
                />
                <AppProviders brand={brand} store={deserializedPageProps.initialData}>
                    <Head>
                        <title>{pageTitle}</title>
                        <meta name="viewport" content="initial-scale=1, width=device-width" />
                        <meta name="theme-color" content={(theme.palette.primary as SimplePaletteColorOptions).main} />
                        <meta name="msapplication-TileColor" content={(theme.palette.primary as SimplePaletteColorOptions).main} />
                        <meta name="msapplication-config" content="/api/browserconfig" />
                        <link rel="shortcut icon" href={`/assets/manifest/${brand}/favicon.ico`} />
                        <link rel="icon" href={`/assets/manifest/${brand}/favicon.svg`} type="image/svg+xml" />
                        <link rel="apple-touch-icon" href={`/assets/manifest/${brand}/apple-touch-icon.png`} />
                        <link rel="manifest" href="/api/manifest" />
                    </Head>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <AgentLogout>
                            <Component {...deserializedPageProps} brand={brand} />
                        </AgentLogout>
                    </ThemeProvider>
                </AppProviders>
            </CacheProvider>
        </JucyUiProvider>
    );
};

export default PortalApp;
